import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { AccordionModule } from 'primeng/accordion';
import { forkJoin } from 'rxjs';
import { SeasonItem, SeasonsService } from '../service/seasons.service';
import { SeasonRuleItem, SeasonRulesService } from '../service/seasonRules.service';
import { RaceDaysDayItem, RaceDaysTableService } from '../service/raceDaysTable.service';
import { LeagueItem, RacesService } from '../service/races.service';
import { RaceDaysOverviewWidgetComponent } from './raceDaysOverview.component';
import { LeagueTable } from './leagueTable.component';
import { LeagueTableV2 } from './leagueTableV2.component';
import { LeagueTableV3 } from './leagueTableV3.component';
import { LeagueRaceTable } from './leagueRaceTable.component';
import { LeagueRaceTableV2 } from './leagueRaceTableV2.component';
import { QualificationTableComponent } from './qualificationTable.component';

/** Which league-table layout a season was raced with. */
type TableVersion = 'v1' | 'v2' | 'v3';

interface RuleDisplayItem {
    rule_id: number;
    label: string;
    value: string;
}

@Component({
    selector: 'app-season-dashboard',
    standalone: true,
    templateUrl: './seasonDashboard.component.html',
    styleUrls: ['./seasonDashboard.component.scss'],
    imports: [
        CommonModule,
        FormsModule,
        RouterLink,
        SelectModule,
        TagModule,
        AccordionModule,
        RaceDaysOverviewWidgetComponent,
        LeagueTable,
        LeagueTableV2,
        LeagueTableV3,
        QualificationTableComponent,
        LeagueRaceTable,
        LeagueRaceTableV2
    ],
    providers: [SeasonsService, SeasonRulesService, RaceDaysTableService, RacesService]
})
export class SeasonDashboardComponent implements OnInit {
    /** Seasons 1+2 were raced in two separate groups (A/B), season 3 in one table
     *  with V2 points; every season from 4 on uses the V3 format. */
    private static readonly LEGACY_TABLE_VERSIONS: Record<number, TableVersion> = { 1: 'v1', 2: 'v1', 3: 'v2' };

    /** Historical group ids of the two-group seasons: Gruppe A = 2, Gruppe B = 1. */
    readonly groupA = 2;
    readonly groupB = 1;

    seasons: SeasonItem[] = [];
    selectedSeasonId: number | null = null;
    season: SeasonItem | null = null;
    loading = true;

    /** All race days across seasons, fetched once and filtered per season. */
    private allRaceDays: RaceDaysDayItem[] | null = null;

    // Hero facts
    startDate: string | null = null;
    endDate: string | null = null;
    circuitCount: number | null = null;
    groupCount: number | null = null;

    // Stat cards
    raceDaysDone = 0;
    raceDaysTotal = 0;
    participantCount: number | null = null;
    leaderName: string | null = null;
    leaderPoints: number | null = null;
    mostWinsNames: string | null = null;
    mostWinsCount = 0;
    // Two-group seasons get one leader card per group instead.
    leaderNameGroupA: string | null = null;
    leaderPointsGroupA: number | null = null;
    leaderNameGroupB: string | null = null;
    leaderPointsGroupB: number | null = null;

    rules: RuleDisplayItem[] = [];

    constructor(
        private seasonsService: SeasonsService,
        private seasonRulesService: SeasonRulesService,
        private raceDaysTableService: RaceDaysTableService,
        private racesService: RacesService,
        private route: ActivatedRoute,
        private router: Router,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit() {
        this.seasonsService.getSeasons().subscribe((seasons) => {
            // Planned seasons stay hidden until they start; newest first for the selector.
            this.seasons = seasons
                .filter((s) => s.status !== 'planning')
                .sort((a, b) => (b.season_id ?? 0) - (a.season_id ?? 0));
            this.route.params.subscribe((params) => {
                const id = +params['season'];
                const visible = this.seasons.some((s) => s.season_id === id);
                this.selectSeason(visible ? id : this.defaultSeasonId());
            });
        });
    }

    get tableVersion(): TableVersion {
        return SeasonDashboardComponent.LEGACY_TABLE_VERSIONS[this.selectedSeasonId ?? 0] ?? 'v3';
    }

    get statusLabel(): string {
        switch (this.season?.status) {
            case 'planning': return 'In Planung';
            case 'active':   return 'Aktiv';
            case 'finished': return 'Beendet';
            default:         return '';
        }
    }

    get statusSeverity(): 'info' | 'danger' | 'success' {
        switch (this.season?.status) {
            case 'planning': return 'danger';
            case 'active':   return 'success';
            default:         return 'info';
        }
    }

    /** Title of the leader stat card: champion once the season is over. */
    get leaderLabel(): string {
        return this.season?.status === 'finished' ? 'Gewinner' : 'Führender';
    }

    get isFinished(): boolean {
        return this.season?.status === 'finished';
    }

    onSeasonChange(seasonId: number) {
        this.router.navigate(['/season', seasonId]);
    }

    private defaultSeasonId(): number {
        const active = this.seasons.find((s) => s.status === 'active');
        return active?.season_id ?? this.seasons[0]?.season_id ?? 1;
    }

    private selectSeason(seasonId: number) {
        this.selectedSeasonId = seasonId;
        this.season = this.seasons.find((s) => s.season_id === seasonId) ?? null;
        this.loading = false;
        this.loadRaceDayFacts(seasonId);
        this.loadRules(seasonId);
        this.loadTableStats(seasonId);
    }

    // ── Hero facts from the season's race days ──────────────────────────────

    private loadRaceDayFacts(seasonId: number) {
        if (this.allRaceDays) {
            this.applyRaceDayFacts(seasonId);
            return;
        }
        this.raceDaysTableService.getRaceDaysTable().subscribe((days) => {
            this.allRaceDays = days;
            this.applyRaceDayFacts(seasonId);
        });
    }

    private applyRaceDayFacts(seasonId: number) {
        const days = (this.allRaceDays ?? [])
            .filter((d) => d.season_id === seasonId)
            .sort((a, b) => new Date(a.day_planned ?? 0).getTime() - new Date(b.day_planned ?? 0).getTime());

        this.startDate = this.season?.start_date ?? days[0]?.day_planned ?? null;
        this.endDate = days.length ? (days[days.length - 1].day_planned ?? null) : null;
        this.circuitCount = days.length
            ? new Set(days.map((d) => d.circuit_name)).size
            : (this.season?.planned_races ?? null);
        this.groupCount = days.length ? new Set(days.map((d) => d.group_name)).size : null;

        // Groups may share a planned date, so "Renntage" counts distinct dates.
        const distinctDates = new Set(days.map((d) => d.day_planned));
        const racedDates = new Set(days.filter((d) => d.day_real).map((d) => d.day_planned));
        this.raceDaysTotal = distinctDates.size;
        this.raceDaysDone = racedDates.size;
        this.cdr.detectChanges();
    }

    // ── Season rules ────────────────────────────────────────────────────────

    private loadRules(seasonId: number) {
        this.rules = [];
        this.seasonRulesService.getRules(seasonId).subscribe((rules) => {
            if (seasonId !== this.selectedSeasonId) return;
            // Pre-catalog seasons stored no values; falling back to today's catalog
            // defaults would misrepresent the rules actually raced under, so the
            // whole block stays hidden unless the season maintained at least one rule.
            const maintained = rules.some((r) => (r.rule_value ?? '').trim() !== '');
            if (!maintained) return;
            this.rules = rules
                .map((r: SeasonRuleItem) => ({
                    rule_id: r.rule_id,
                    label: r.rule_label,
                    value: (r.rule_value ?? r.default_value ?? '').trim()
                }))
                .filter((r) => r.value !== '');
            this.cdr.detectChanges();
        });
    }

    // ── Standings-based stats (participants, leader, most wins) ─────────────

    private loadTableStats(seasonId: number) {
        this.participantCount = null;
        this.leaderName = null;
        this.leaderPoints = null;
        this.mostWinsNames = null;
        this.mostWinsCount = 0;
        this.leaderNameGroupA = null;
        this.leaderPointsGroupA = null;
        this.leaderNameGroupB = null;
        this.leaderPointsGroupB = null;

        const season = String(seasonId);
        if (this.tableVersion === 'v1') {
            forkJoin({
                groupA: this.racesService.getLeagueTableForSeason(season, String(this.groupA)),
                groupB: this.racesService.getLeagueTableForSeason(season, String(this.groupB))
            }).subscribe(({ groupA, groupB }) => {
                if (seasonId !== this.selectedSeasonId) return;
                this.participantCount = groupA.length + groupB.length;
                const leaderA = this.findLeader(groupA);
                const leaderB = this.findLeader(groupB);
                this.leaderNameGroupA = leaderA?.name_gt7 ?? null;
                this.leaderPointsGroupA = leaderA?.points_total ?? null;
                this.leaderNameGroupB = leaderB?.name_gt7 ?? null;
                this.leaderPointsGroupB = leaderB?.points_total ?? null;
                this.cdr.detectChanges();
            });
            return;
        }

        const table$ = this.tableVersion === 'v2'
            ? this.racesService.getLeagueTableForSeasonV2(season)
            : this.racesService.getLeagueTableForSeasonV3(season);
        const pointsKey = this.tableVersion === 'v2' ? 'points_v2' : 'points_v3';

        table$.subscribe((players) => {
            if (seasonId !== this.selectedSeasonId) return;
            this.participantCount = players.length;
            const leader = this.findLeader(players);
            this.leaderName = leader?.name_gt7 ?? null;
            this.leaderPoints = leader?.points_total ?? null;
            this.computeMostWins(players, pointsKey);
            this.cdr.detectChanges();
        });
    }

    private findLeader(players: LeagueItem[]): LeagueItem | null {
        const ranked = players.filter((p) => p.final_position != null);
        if (!ranked.length) return null;
        return ranked.reduce((best, p) => ((p.final_position ?? 0) < (best.final_position ?? 0) ? p : best));
    }

    /** Race-day winner = highest points of that day; the card shows who won most days. */
    private computeMostWins(players: LeagueItem[], pointsKey: 'points_v2' | 'points_v3') {
        const dayIds = new Set<string>();
        players.forEach((p) => p.races?.forEach((r) => dayIds.add(r.day_id)));

        const winCounts = new Map<number, number>();
        dayIds.forEach((dayId) => {
            let maxPoints = -1;
            let winners: number[] = [];
            players.forEach((p) => {
                const points = p.races?.find((r) => r.day_id === dayId)?.[pointsKey];
                if (points === undefined || points === null) return;
                if (points > maxPoints) {
                    maxPoints = points;
                    winners = [p.player_id!];
                } else if (points === maxPoints) {
                    winners.push(p.player_id!);
                }
            });
            winners.forEach((id) => winCounts.set(id, (winCounts.get(id) ?? 0) + 1));
        });

        let best = 0;
        winCounts.forEach((count) => (best = Math.max(best, count)));
        if (best === 0) return;

        const names = players
            .filter((p) => winCounts.get(p.player_id!) === best)
            .map((p) => p.name_gt7)
            .filter((n): n is string => !!n);
        this.mostWinsCount = best;
        this.mostWinsNames = names.join(', ');
    }
}
